import { CommonModule } from '@angular/common';
import { Component, forwardRef, Input } from '@angular/core';
import {
    ControlValueAccessor,
    FormControl,
    FormGroup,
    FormsModule,
    NG_VALUE_ACCESSOR,
    ReactiveFormsModule,
    Validators,
} from '@angular/forms';
import { IonContent, IonModal } from '@ionic/angular/standalone';
import { _, TranslateModule } from '@ngx-translate/core';
import { getActiveMarket } from '../../config/markets.config';

/**
 * A complete Desi entry (integer or decimal, `.` or `,`) becomes that number.
 * Empty and unfinished text (`3.`, `3,`) become null so the field is not forced to 0.
 * Callers keep the raw text on screen.
 */
export function parseDeciInput(raw: string): number | null {
    const trimmed = raw.trim();
    if (!/^[+-]?(?:\d+(?:[.,]\d+)?|[.,]\d+)$/.test(trimmed)) {
        return null;
    }
    const value = Number(trimmed.replace(',', '.'));
    return Number.isFinite(value) ? value : null;
}

@Component({
    selector: 'app-deci-helper',
    templateUrl: './deci-helper.component.html',
    styleUrls: ['./deci-helper.component.scss'],
    imports: [CommonModule, CommonModule, TranslateModule, ReactiveFormsModule, FormsModule, IonModal, IonContent],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => DeciHelperComponent),
            multi: true,
        },
    ],
})
export class DeciHelperComponent implements ControlValueAccessor {
    @Input() placeholder: string = _('Deci');
    @Input() isInvalid: boolean = false;
    /** Text shown in the field. Kept separate from the number emitted to the form. */
    displayValue = '';
    isModalOpen = false;
    form = new FormGroup({
        width: new FormControl(0, [Validators.required, Validators.min(1)]),
        height: new FormControl(0, [Validators.required, Validators.min(1)]),
        depth: new FormControl(0, [Validators.required, Validators.min(1)]),
    });

    onInputChange(event: Event) {
        const raw = (event.target as HTMLInputElement).value;
        this.displayValue = raw;
        this.onChange(parseDeciInput(raw));
        this.onTouched();
    }

    private onChange: (value: number | null) => void = () => {};
    private onTouched: () => void = () => {};

    writeValue(value: number | string | null): void {
        if (value === null || value === undefined || value === '') {
            this.displayValue = '';
            return;
        }
        const numeric = typeof value === 'number' ? value : Number(String(value).trim().replace(',', '.'));
        this.displayValue = Number.isFinite(numeric) ? String(numeric) : '';
    }

    registerOnChange(fn: (value: number | null) => void): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    setModalOpen(isOpen: boolean) {
        this.isModalOpen = isOpen;
    }

    submit() {
        if (this.form.invalid) {
            this.form.markAllAsTouched();
            return;
        }
        const divisor = getActiveMarket().volumetricDivisor;
        if (!divisor) {
            this.onTouched();
            this.setModalOpen(false);
            return;
        }
        const deci = parseFloat(((this.form.value.width * this.form.value.height * this.form.value.depth) / divisor).toFixed(2));
        this.displayValue = String(deci);
        this.onChange(deci);
        this.onTouched();
        this.setModalOpen(false);
    }
}
